import React, { useState } from "react";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Eye, Pencil, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { validateDate } from "@/utils/dashboardUtils";
import { DeleteConfirmationDialog } from "@/components/DeleteConfirmationDialog";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface UserData {
  id: string;
  name: string;
  role: 'admin' | 'user';
  createdAt: Date;
  lastActive?: Date;
  totalRides: number;
  totalDistance: number;
  organizationName?: string;
}

interface UsersTableProps {
  users: UserData[];
  isLoading: boolean;
  currentPage: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onRefresh: () => void;
}

const UsersTable: React.FC<UsersTableProps> = ({
  users,
  isLoading,
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onRefresh,
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserData | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { user, deleteUser } = useAuth();
  
  const formatSafeDate = (date: Date | string | null | undefined) => {
    if (!date) return "N/A";
    
    const parsedDate = validateDate(date);
    return format(parsedDate, "dd MMM yyyy");
  };
  
  const handleDeleteClick = (userData: UserData) => {
    setUserToDelete(userData);
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteConfirm = async () => {
    if (userToDelete && !isDeleting) {
      try {
        setIsDeleting(true);
        
        await deleteUser(userToDelete.id);
        
        toast.success("User deleted successfully");
        
        setDeleteDialogOpen(false);
        setUserToDelete(null);
        setIsDeleting(false);
        
        setTimeout(() => {
          onRefresh();
        }, 100);
      } catch (error) {
        console.error("Error deleting user:", error);
        toast.error("Failed to delete user. Please try again.");
        
        setDeleteDialogOpen(false);
        setUserToDelete(null);
        setIsDeleting(false);
      }
    }
  };

  const totalPages = Math.ceil(totalItems / pageSize);
  
  if (isLoading) {
    return (
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Organization</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell><Skeleton className="h-5 w-[150px]" /></TableCell>
                <TableCell><Skeleton className="h-5 w-[80px]" /></TableCell>
                <TableCell><Skeleton className="h-5 w-[100px]" /></TableCell>
                <TableCell><Skeleton className="h-5 w-[100px]" /></TableCell>
                <TableCell><Skeleton className="h-9 w-[120px]" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }
  
  if (users.length === 0) {
    return (
      <div className="rounded-md border p-8 text-center">
        <div className="text-lg font-medium">No Users Found</div>
        <div className="text-muted-foreground mt-2">Try adjusting your search or filters</div>
        <Button onClick={onRefresh} variant="outline" className="mt-4">
          Refresh Data
        </Button>
      </div>
    );
  }
  
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Organization</TableHead>
            <TableHead>Created</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((userData) => (
            <TableRow key={userData.id}>
              <TableCell>{userData.name}</TableCell>
              <TableCell>
                {userData.role === "admin" ? (
                  <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200">Admin</Badge>
                ) : (
                  <Badge variant="outline">User</Badge>
                )}
              </TableCell>
              <TableCell>{userData.organizationName || "-"}</TableCell>
              <TableCell>{formatSafeDate(userData.createdAt)}</TableCell>
              <TableCell>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    asChild
                    className="h-8 w-8 p-0"
                  >
                    <Link to={`/users/${userData.id}`}>
                      <Eye className="h-4 w-4" />
                      <span className="sr-only">View</span>
                    </Link>
                  </Button>
                  {(user?.role === "admin" && userData.role !== "admin") || user?.id === userData.id ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      asChild
                      className="h-8 w-8 p-0"
                    >
                      <Link to={`/users/${userData.id}/edit`}>
                        <Pencil className="h-4 w-4" />
                        <span className="sr-only">Edit</span>
                      </Link>
                    </Button>
                  ) : null}
                  {user?.role === "admin" && userData.role !== "admin" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 hover:bg-destructive/10 hover:text-destructive"
                      onClick={() => handleDeleteClick(userData)}
                      disabled={isDeleting}
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="sr-only">Delete</span>
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t px-4 py-2">
          <div className="text-sm text-muted-foreground">
            Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{" "}
            <span className="font-medium">
              {Math.min(currentPage * pageSize, totalItems)}
            </span>{" "}
            of <span className="font-medium">{totalItems}</span> users
          </div>
          <div className="space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1 || isDeleting}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages || isDeleting}
            >
              Next
            </Button>
          </div>
        </div>
      )}
      
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (!isDeleting) {
            setDeleteDialogOpen(open);
            if (!open) {
              setUserToDelete(null);
            }
          }
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete User Account"
        description={`Are you sure you want to delete the account for ${userToDelete?.name || ''}? This action cannot be undone.`}
      />
    </div>
  );
};

export default UsersTable;
